import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ForecastCard from "@/components/feature/ForecastCard";
import SubscribeForm from "@/components/feature/SubscribeForm";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories, forecasts } from "@/mocks/forecast";

type SortKey = "heat" | "latest";

export default function ForecastList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const keyword = searchParams.get("q") || "";
  const [sortKey, setSortKey] = useState<SortKey>("heat");
  const [searchInput, setSearchInput] = useState(keyword);

  usePageMeta({
    title: "预告前瞻列表 - 看料大瓜 | 实时瓜田预警与蹲守线索",
    description:
      "浏览看料大瓜的全部预告前瞻，按影视圈、音乐圈、综艺坊、网红直播、商业瓜等分类筛选，查看正在发酵的瓜田预警与蹲守线索，快人一步掌握潜在爆料。",
    keywords: "看料大瓜预告,看料大瓜前瞻,瓜田预警,蹲守线索,预告前瞻列表",
    path: "/forecast",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "预告前瞻列表",
        description: "看料大瓜全部预告前瞻与瓜田预警汇总。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "预告前瞻", item: "/forecast" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "预告前瞻条目",
        itemListElement: forecasts.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `/forecast/${item.id}`,
        })),
      },
    ],
  });

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "all") params.delete("category");
      else params.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const filtered = forecasts.filter((item) => {
      const matchCategory = activeCategory === "all" || item.categoryId === activeCategory;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.excerpt.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
    return [...filtered].sort((a, b) =>
      sortKey === "heat" ? b.heat - a.heat : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [activeCategory, keyword, sortKey]);

  const activeCategoryName =
    activeCategory === "all"
      ? "全部分类"
      : categories.find((cat) => cat.id === activeCategory)?.name || "全部分类";

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "预告前瞻" }]} />
          <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-5xl">
            预告前瞻列表
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里汇总了当前正在监测的全部瓜田预警。每条前瞻都附带信号清单与时间线，你可以按分类筛选，或直接搜索关键词找到感兴趣的方向。
          </p>
        </div>
      </section>

      <section className="sticky top-16 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur md:top-20">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateParams({ category: "all" })}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                activeCategory === "all"
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => updateParams({ category: cat.id })}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  activeCategory === cat.id
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <form
              className="relative flex-1 md:w-64"
              onSubmit={(event) => {
                event.preventDefault();
                updateParams({ q: searchInput.trim() });
              }}
            >
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="搜索关键词，如「换角」"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </form>
            <div className="flex shrink-0 items-center rounded-full bg-background-100 px-1 py-1">
              <button
                type="button"
                onClick={() => setSortKey("heat")}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  sortKey === "heat" ? "bg-background-50 text-primary-600" : "text-foreground-600"
                }`}
              >
                热度优先
              </button>
              <button
                type="button"
                onClick={() => setSortKey("latest")}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  sortKey === "latest" ? "bg-background-50 text-primary-600" : "text-foreground-600"
                }`}
              >
                最新发布
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <p className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
            <span className="font-semibold text-foreground-900">{activeCategoryName}</span>
            <span className="text-foreground-400">·</span>
            <span>共 {results.length} 条前瞻</span>
            {keyword && (
              <span className="flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                关键词：{keyword}
                <button
                  type="button"
                  aria-label="清除关键词"
                  onClick={() => {
                    setSearchInput("");
                    updateParams({ q: "" });
                  }}
                  className="flex h-4 w-4 items-center justify-center"
                >
                  <i className="ri-close-line" />
                </button>
              </span>
            )}
          </p>

          {results.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {results.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <ForecastCard item={item} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">暂时没有匹配的前瞻</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词，或者切换到其他分类看看。你也可以订阅蹲守提醒，有新前瞻时我们会主动通知你。
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  updateParams({ q: "", category: "all" });
                }}
                className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                想先人一步收到新前瞻？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                留下邮箱并选择你关注的分类，新的预警发布时我们会第一时间提醒你。
              </p>
            </div>
            <div className="lg:col-span-7">
              <SubscribeForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}